/**
 * Small pieces the Testing screen's tabs share: status chips, the screenshot
 * lightbox, table styles and the issues table.
 */

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import type { Issue } from '@/api/uiTesting'

export const tableStyle: CSSProperties = {
  width: '100%', borderCollapse: 'collapse', fontSize: 14,
}
export const thStyle: CSSProperties = {
  textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)',
  color: 'var(--color-text-secondary)', fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap',
  background: 'var(--color-bg-secondary)', position: 'sticky', top: 0, zIndex: 1,
}
export const tdStyle: CSSProperties = {
  padding: '6px 8px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'top',
}
export const mutedText: CSSProperties = { color: 'var(--color-text-secondary)', fontSize: 13 }
export const cardStyle: CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 8, padding: 14,
  background: 'var(--color-bg-secondary)',
}
export const inputStyle: CSSProperties = {
  padding: '6px 8px', borderRadius: 6, border: '1px solid var(--color-border-strong)',
  background: 'var(--color-input-bg)', color: 'var(--color-text)', fontSize: 14,
}

const TONES: Record<string, string> = {
  passed: 'var(--color-success, #16a34a)',
  completed: 'var(--color-success, #16a34a)',
  logged: 'var(--color-accent)',
  failed: 'var(--color-error, #dc2626)',
  critical: 'var(--color-error, #dc2626)',
  high: 'var(--color-error, #dc2626)',
  open: 'var(--color-warning, #d97706)',
  blocked: 'var(--color-warning, #d97706)',
  medium: 'var(--color-warning, #d97706)',
  running: 'var(--color-accent)',
  queued: 'var(--color-accent)',
  interrupted: 'var(--color-warning, #d97706)',
  cancelled: 'var(--color-text-secondary)',
  closed: 'var(--color-text-secondary)',
  skipped: 'var(--color-text-secondary)',
  low: 'var(--color-text-secondary)',
}

export const STATUS_LABEL: Record<string, string> = {
  passed: 'Passed', failed: 'Failed', blocked: 'Blocked', skipped: 'Not run',
  queued: 'Queued', running: 'Running', completed: 'Completed', cancelled: 'Cancelled',
  interrupted: 'Interrupted', open: 'Open', logged: 'Logged', closed: 'Closed',
}

export function Chip({ value, label }: { value: string; label?: string }) {
  const c = TONES[value] || 'var(--color-text-secondary)'
  return (
    <span style={{
      display: 'inline-block', padding: '1px 8px', borderRadius: 10, fontSize: 13,
      fontWeight: 600, color: c, border: `1px solid ${c}`, whiteSpace: 'nowrap',
    }}>{label ?? STATUS_LABEL[value] ?? value}</span>
  )
}

export function Lightbox({ src, caption, onClose, onPrev, onNext, position }: {
  src: string | null; caption?: string; onClose: () => void
  /** Step to the previous / next picture of the same set (absent ⇒ no button). */
  onPrev?: () => void
  onNext?: () => void
  /** "3 of 12" — where this picture sits in its set. */
  position?: string
}) {
  useEffect(() => {
    if (!src) return
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose() }
      else if (e.key === 'ArrowLeft' && onPrev) { e.preventDefault(); onPrev() }
      else if (e.key === 'ArrowRight' && onNext) { e.preventDefault(); onNext() }
    }
    // Capture: the lightbox sits over a modal whose own Escape would close it too.
    window.addEventListener('keydown', k, true)
    return () => window.removeEventListener('keydown', k, true)
  }, [src, onClose, onPrev, onNext])
  if (!src) return null
  const navBtn: CSSProperties = {
    color: '#fff', background: 'rgba(0,0,0,0.45)', borderRadius: '50%', width: 44, height: 44,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flex: 'none', padding: 0,
  }
  return (
    <div className="overlay" onClick={onClose} style={{ zIndex: 2000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        maxWidth: '94vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', gap: 6,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, color: '#fff', fontSize: 13 }}>
          <span>{position ? <strong style={{ marginRight: 8 }}>{position}</strong> : null}{caption}</span>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close"
                  style={{ color: '#fff' }}><X size={16} /></button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {(onPrev || onNext) && (
            <button className="btn btn-ghost" onClick={onPrev} disabled={!onPrev}
                    aria-label="Previous picture" title="Previous (←)" data-tw-action="lightbox-prev"
                    style={{ ...navBtn, opacity: onPrev ? 1 : 0.3 }}><ChevronLeft size={22} /></button>
          )}
          <img src={src} alt={caption || 'screenshot'}
               style={{ maxWidth: (onPrev || onNext) ? 'calc(94vw - 104px)' : '94vw', maxHeight: '86vh', objectFit: 'contain', borderRadius: 6 }} />
          {(onPrev || onNext) && (
            <button className="btn btn-ghost" onClick={onNext} disabled={!onNext}
                    aria-label="Next picture" title="Next (→)" data-tw-action="lightbox-next"
                    style={{ ...navBtn, opacity: onNext ? 1 : 0.3 }}><ChevronRight size={22} /></button>
          )}
        </div>
      </div>
    </div>
  )
}

/** A thumbnail that opens the lightbox; hides itself if the file is missing. */
export function Thumb({ src, caption, onOpen, width = 120, fluid, onMissing }: {
  src: string; caption?: string; onOpen: (src: string, caption?: string) => void; width?: number
  /** Fill the parent's width at 16:10 instead of a fixed pixel width. */
  fluid?: boolean
  onMissing?: () => void
}) {
  const [missing, setMissing] = useState(false)
  if (missing) return null
  return (
    <img
      src={src} alt={caption || 'screenshot'} title={caption}
      onError={() => { setMissing(true); onMissing?.() }}
      onClick={() => onOpen(src, caption)}
      style={fluid ? {
        width: '100%', aspectRatio: '16 / 10', objectFit: 'cover', objectPosition: 'top', cursor: 'zoom-in',
        display: 'block',
      } : {
        width, height: 'auto', maxHeight: width * 0.7, objectFit: 'cover', cursor: 'zoom-in',
        border: '1px solid var(--color-border)', borderRadius: 4,
      }}
    />
  )
}

export function IssuesTable({ issues }: { issues: Issue[] }) {
  if (!issues.length) return <div style={mutedText}>No issues.</div>
  return (
    <div style={{ overflowX: 'auto', maxHeight: 360 }}>
      <table style={tableStyle}>
        <thead><tr>
          <th style={thStyle}>Case</th><th style={thStyle}>Where</th>
          <th style={thStyle}>Severity</th><th style={thStyle}>Message</th>
        </tr></thead>
        <tbody>
          {issues.map((i, n) => (
            <tr key={n}>
              <td style={tdStyle}><code>{i.case}</code></td>
              <td style={tdStyle}>{i.where}</td>
              <td style={tdStyle}><Chip value={i.severity === 'error' ? 'failed' : 'blocked'}
                                       label={i.severity} /></td>
              <td style={tdStyle}>{i.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function Section({ title, actions, children }: {
  title: ReactNode; actions?: ReactNode; children: ReactNode
}) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
        flexWrap: 'wrap', marginBottom: 8,
      }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{title}</h3>
        {actions && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{actions}</div>}
      </div>
      {children}
    </div>
  )
}

export function fmtTime(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

// ── the redesigned screen's pieces (docs/ui_acceptance_testing.md) ─────────

/** A panel: the page background steps up to the secondary surface. */
export const panel: CSSProperties = {
  background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
}

export const h2Style: CSSProperties = { margin: 0, fontSize: 18, fontWeight: 600 }
export const labelCaps: CSSProperties = {
  color: 'var(--color-text-secondary)', fontSize: 13, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.06em',
}

export type Tone = 'ok' | 'err' | 'warn' | 'info' | 'neutral'

/** Strong (text/fill) and weak (background) colour of a tone — theme tokens. */
export const TONE: Record<Tone, { fg: string; bg: string }> = {
  ok: { fg: 'var(--color-success)', bg: 'var(--color-success-weak)' },
  err: { fg: 'var(--color-error)', bg: 'var(--color-error-weak)' },
  warn: { fg: 'var(--color-warning)', bg: 'var(--color-warning-weak)' },
  info: { fg: 'var(--color-accent)', bg: 'var(--color-accent-weak)' },
  neutral: { fg: 'var(--color-text-secondary)', bg: 'var(--color-secondary-btn)' },
}

export const caseTone = (s?: string): Tone =>
  s === 'passed' ? 'ok' : s === 'failed' ? 'err' : s === 'blocked' ? 'warn' : 'neutral'

/** A filled pill: status words, counts, tags. */
export function Badge({ tone = 'neutral', children, style }: {
  tone?: Tone; children: ReactNode; style?: CSSProperties
}) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 9px',
      borderRadius: 'var(--radius-full)', fontSize: 13, fontWeight: 600, lineHeight: 1,
      whiteSpace: 'nowrap', color: TONE[tone].fg, background: TONE[tone].bg, ...style,
    }}>{children}</span>
  )
}

export function Dot({ tone, size = 10 }: { tone: Tone; size?: number }) {
  return <span aria-hidden="true" style={{
    width: size, height: size, borderRadius: '50%', flex: 'none', display: 'inline-block',
    background: TONE[tone].fg,
  }} />
}

/** A small numbered circle (step lists). */
export function NumberDot({ n, tone, size = 24 }: { n: ReactNode; tone: Tone; size?: number }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: '50%', flex: 'none', display: 'inline-flex',
      alignItems: 'center', justifyContent: 'center', fontSize: size > 26 ? 14 : 12, fontWeight: 700,
      background: TONE[tone].bg, color: TONE[tone].fg,
    }}>{n}</span>
  )
}

/** A horizontal bar split into coloured shares. */
export function ShareBar({ parts, height = 10, label }: {
  parts: { value: number; tone: Tone }[]; height?: number; label: string
}) {
  const total = parts.reduce((a, p) => a + p.value, 0)
  return (
    <div role="img" aria-label={label} style={{
      display: 'flex', height, borderRadius: 'var(--radius-full)', overflow: 'hidden',
      background: 'var(--color-secondary-btn)',
    }}>
      {total > 0 && parts.filter((p) => p.value > 0).map((p, i) => (
        <div key={i} style={{ width: `${(p.value / total) * 100}%`, background: TONE[p.tone].fg }} />
      ))}
    </div>
  )
}

/** "2 hours ago", falling back to the date for anything older than a week. */
export function ago(iso?: string | null): string {
  if (!iso) return '—'
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return iso
  const s = Math.max(0, (Date.now() - t) / 1000)
  if (s < 60) return 'just now'
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`
  const d = Math.round(h / 24)
  if (d < 7) return d === 1 ? 'yesterday' : `${d} days ago`
  return new Date(t).toLocaleDateString()
}

/** "4 min 12 s" between two instants, or '' when either is missing. */
export function took(from?: string | null, to?: string | null): string {
  if (!from || !to) return ''
  const s = Math.round((new Date(to).getTime() - new Date(from).getTime()) / 1000)
  if (!Number.isFinite(s) || s < 0) return ''
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`
}

/**
 * Hand a test-writing request to the Ask-AI dock (it routes acceptance-test
 * work to its `acceptance-test-designer`). The dock only takes it for the
 * project it is open on, so this says so when nothing picks it up.
 */
export function askAiToWriteTests(project: string, focus?: string): void {
  const prompt = `Write UI acceptance test cases for the application "${project}"`
    + (focus ? ` — ${focus}.` : ', starting with the requirements and business rules that have no test yet.')
    + ' Base them on the requirement specification and the screens, validate them, and tell me what you added.'
  window.dispatchEvent(new CustomEvent('twasta:agent-run-task', { detail: { project, prompt } }))
}

/**
 * Hand ONE failed case to the Ask-AI dock to fix in place — the developer's
 * path, with no Support ticket. The agent reads the case's evidence, fixes the
 * application (or the case, when the case is what is wrong), and re-runs just
 * that case to prove it.
 */
export function askAiToFixCase(project: string, runId: string, caseId: string,
                               title: string, failure: string, obj?: string): void {
  const prompt = `Fix the failing UI acceptance test ${caseId} ("${title}") of the application `
    + `"${project}", from run ${runId}${obj ? ` — it tests the object ${obj}` : ''}. `
    + `It failed with: ${failure || 'see the evidence'}.\n`
    + `Read its evidence first (\`ui_test.py case ${runId} ${caseId}\` — the steps, screenshots, `
    + 'console, network and server log). Decide whether the APPLICATION is wrong or the TEST CASE is.\n'
    + 'Fix the application by DELEGATING to the sub-agent that owns the broken part — never edit a '
    + 'model yourself: a transaction screen (columns, validations, look-ups, actions) → '
    + '`transaction-designer`; its business-logic code → `business-logic-coder`; an approval → '
    + '`approval-workflow-designer`; a visual / dashboard / visual schema → `visual-analytics-designer`; '
    + 'a report → `report-designer`; a general process → `general-process-designer`; a wizard → '
    + '`wizard-designer`; a smart page or website page → `smart-page-designer`; a document form → '
    + '`document-form-designer`; rights / roles → `access-manager`. '
    + 'If the case itself is wrong (a field or expectation the requirements do not support), fix the '
    + 'case through `acceptance-test-designer` instead and say so.\n'
    + 'Stay inside what this case tests. Never create or change an approval workflow (the model\'s '
    + '`transaction.approval`, any `Workflow_Design` XPDL), roles or rights, or the test plan\'s '
    + 'personas to make the case pass, unless the case is about exactly that. An approval-controlled '
    + 'save answering "Submitted for approval" is correct: the test approves it itself as the '
    + 'super-approver ("Show all pending approvals") and then checks the saved data. If a step fails '
    + 'only because of the approval inbox or the environment (no published version, permission denied, '
    + 'cannot connect), report it as a test-tool / environment problem and ask me — do not change the '
    + 'application for it.\n'
    + `Then re-run only this case (\`ui_test.py run --cases ${caseId}\`) and tell me whether it passes now. `
    + 'If you cannot fix it, or the fix needs a decision from me, ask me — do not stop silently.'
  window.dispatchEvent(new CustomEvent('twasta:agent-run-task', { detail: { project, prompt } }))
}

/** One failed unit-test check handed to Ask-AI. */
export interface UnitFixItem {
  obj: string
  title: string
  /** The check's label ("Add, list, open, edit and delete a record", "Action 'Mark Lost'"). */
  check: string
  step?: string
  message?: string
}

/**
 * Hand failed UNIT-test checks — one, or every failure — to the Ask-AI dock to
 * fix in place. The agent reads each screen's kept result, decides whether the
 * application or only the made-up test data is wrong, delegates the fix to the
 * owning sub-agent, and re-runs each changed screen's unit test on the model's
 * own data, which the Unit tests tab then shows.
 */
export function askAiToFixUnitChecks(project: string, items: UnitFixItem[]): void {
  if (!items.length) return
  const one = items.length === 1
  const list = items.map((it, i) => `${i + 1}. ${it.title} (${it.obj}) — ${it.check}: failed`
    + `${it.step ? ` at "${it.step}"` : ''}${it.message ? ` — ${it.message}` : ''}`).join('\n')
  const objs = Array.from(new Set(items.map((it) => it.obj)))
  const prompt = `Fix ${one ? 'this failing unit-test check' : `these ${items.length} failing unit-test checks`} `
    + `of the application "${project}". The unit test runs on the server through the application's own `
    + 'engine: it adds a made-up record, lists, opens, edits and deletes it, then runs each action button '
    + 'on a new record its rules accept.\n'
    + `${list}\n`
    + 'For each, read the kept result first (`Test_Results/unit_tests/<object>.json` — the report\'s '
    + '`checks` and `steps`) and decide whether the APPLICATION is wrong or only the made-up TEST DATA is.\n'
    + '- Test data (a look-up value that does not exist, a format the made-up value misses): delegate to '
    + '`unit-test-runner`, which fixes the data and keeps a working sample in `transaction.unit_test.sample`.\n'
    + '- The application: delegate to the sub-agent that owns the broken part — never edit a model '
    + 'yourself: an action\'s or validation\'s business-logic code → `business-logic-coder`; columns, '
    + 'validations, look-ups, action definitions → `transaction-designer`; an approval → '
    + '`approval-workflow-designer`.\n'
    + 'Do not create or change approval workflows, roles or rights to make a test pass.\n'
    + `Then re-run the unit test of ${objs.length === 1 ? `${objs[0]}` : `each of ${objs.join(', ')}`} `
    + 'through `unit-test-runner` (`run <object>`, the final run WITHOUT --sample, so the Unit tests tab '
    + 'records it) and tell me which checks pass now. If a fix needs a decision from me, ask me — '
    + 'do not stop silently.'
  window.dispatchEvent(new CustomEvent('twasta:agent-run-task', { detail: { project, prompt } }))
}
